<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import {
  BanknotesIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import { useRegulationStore } from '@/stores/useRegulationStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useToast } from 'vue-toast-notification'
import { REGULATION_STATUS } from '@/constants/regulationStatus'
import { formatDate } from '@/utils/dateUtils'

const route = useRoute()
const router = useRouter()
const regulationStore = useRegulationStore()
const boostCoreStore = useBoostCorePermissionStore()
const $toast = useToast()

const regulationUuid = computed(() => route.params.uuid as string)
const regulation = ref<any | null>(null)
const isLoading = ref(false)
const showModalCancel = ref(false)

const canAdvWrite = computed(() => boostCoreStore.can('ADV_WRITE'))
const isWaiting = computed(() => regulation.value?.status === REGULATION_STATUS.WAITING)

const orderComments = computed(() => {
  const order = regulation.value?.quote?.order
  return order?.orderComments ?? []
})

onMounted(async () => {
  if (!regulationUuid.value) return
  isLoading.value = true
  regulation.value = await regulationStore.fetchRegulation(regulationUuid.value)
  isLoading.value = false
})

function goBack() {
  router.push('/dashboard/orders')
}

function goToOrder() {
  const orderUuid = regulation.value?.quote?.order?.uuid
  if (orderUuid) router.push(`/dashboard/order/${orderUuid}`)
}

async function onValidate() {
  if (!regulation.value?.uuid || !isWaiting.value || !canAdvWrite.value) return
  const updated = await regulationStore.updateRegulationStatus(
    regulation.value.uuid,
    REGULATION_STATUS.VALIDATED,
    undefined,
    boostCoreStore.user?.uuid,
  )
  if (updated) {
    regulation.value = updated
    $toast.success('Régularisation validée')
  } else {
    $toast.error('Erreur lors de la validation')
  }
}

function onCancelClick() {
  if (!canAdvWrite.value) return
  showModalCancel.value = true
}

async function onConfirmCancel() {
  showModalCancel.value = false
  if (!regulation.value?.uuid || !isWaiting.value) return
  const updated = await regulationStore.updateRegulationStatus(regulation.value.uuid, REGULATION_STATUS.CANCELED)
  if (updated) {
    regulation.value = updated
    $toast.success('Régularisation annulée')
  } else {
    $toast.error("Erreur lors de l'annulation")
  }
}
</script>

<template>
  <div class="min-h-screen bg-base-200/40">
    <div class="px-4 py-6 max-w-[1200px] mx-auto w-full">
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-1">
            <h1 class="text-2xl font-semibold">Détail de la régularisation</h1>
            <HelpButton />
          </div>
          <p class="text-sm text-base-content/70">Consultation des informations, validation et annulation</p>
        </div>
        <div class="flex items-center gap-2 flex-wrap">
          <ButtonDaisy text="Retour" variant="ghost" @click="goBack" />
          <ButtonDaisy
            v-if="isWaiting && canAdvWrite"
            text="Valider"
            variant="primary"
            :icon="CheckCircleIcon"
            @click="onValidate"
          />
          <ButtonDaisy
            v-if="isWaiting && canAdvWrite"
            text="Annuler"
            variant="ghost"
            :icon="XCircleIcon"
            @click="onCancelClick"
          />
        </div>
      </div>

      <div v-if="isLoading" class="mt-6 card bg-base-100 shadow">
        <div class="card-body">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      </div>

      <div v-else-if="regulation" class="mt-6 space-y-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <h2 class="card-title text-lg gap-2">
                <BanknotesIcon class="w-5 h-5" />
                Régularisation
              </h2>
              <div class="p-4 bg-base-200/50 rounded-lg mt-2 space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Statut</span>
                  <span
                  class="badge badge-sm"
                  :class="{
                    'badge-warning': regulation.status === REGULATION_STATUS.WAITING,
                    'badge-success': regulation.status === REGULATION_STATUS.VALIDATED,
                    'badge-error': regulation.status === REGULATION_STATUS.CANCELED,
                  }"
                >
                  {{ regulation.status }}
                </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Montant</span>
                  <span class="font-semibold" :class="regulation.amount < 0 ? 'text-error' : 'text-success'">
                    {{ regulation.amount?.toFixed(2) }} €
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Commercial</span>
                  <span class="font-medium">
                    {{ regulation.salesEngineer?.firstName }} {{ regulation.salesEngineer?.lastName }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Auteur</span>
                  <span class="font-medium">
                    {{ regulation.author?.firstName }} {{ regulation.author?.lastName }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Date création</span>
                  <span>{{ formatDate(regulation.createdAt?.toString() || '') }}</span>
                </div>
                <div v-if="regulation.validatedAt" class="flex items-center justify-between">
                  <span class="text-sm opacity-70">Date validation</span>
                  <span>{{ formatDate(regulation.validatedAt?.toString() || '') }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <h2 class="card-title text-lg gap-2">
                <DocumentTextIcon class="w-5 h-5" />
                Devis
              </h2>
              <div v-if="regulation.quote" class="p-4 bg-base-200/50 rounded-lg mt-2 space-y-3">
                <div class="flex items-center gap-2">
                  <span class="font-semibold">{{ regulation.quote.quoteReference }}</span>
                  <a
                    v-if="(regulation.quote as any).odooUrl || regulation.quote.metadata?.odooUrl"
                    :href="(regulation.quote as any).odooUrl || regulation.quote.metadata.odooUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-primary underline opacity-70 hover:opacity-100 flex items-center gap-0.5"
                  >
                    <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    Odoo
                  </a>
                </div>
                <div v-if="regulation.quote.company">
                  <span class="text-sm opacity-70">Client</span>
                  <p class="font-medium">{{ regulation.quote.company.name }}</p>
                </div>
                <ButtonDaisy
                  v-if="regulation.quote?.order?.uuid"
                  text="Voir la commande"
                  variant="outline"
                  size="sm"
                  @click="goToOrder"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg gap-2">
              <ChatBubbleLeftRightIcon class="w-5 h-5" />
              Motif / Commentaires
            </h2>
            <div v-if="regulation.reason" class="p-4 bg-base-200/50 rounded-lg mt-2">
              <p class="text-sm font-medium opacity-70 mb-1">Motif de la régularisation</p>
              <p class="text-sm whitespace-pre-wrap">{{ regulation.reason }}</p>
            </div>
            <div v-if="orderComments.length" class="mt-4 space-y-4">
              <p class="text-sm font-medium opacity-70">Commentaires de la commande</p>
              <div v-for="(c, i) in orderComments" :key="c.uuid || i" class="p-4 bg-base-200/50 rounded-lg border border-base-300">
                <div class="flex items-center justify-between gap-2 mb-2">
                  <span class="font-semibold text-sm">
                    {{ c.author?.firstName }} {{ c.author?.lastName }}
                    <span v-if="!c.author?.firstName && !c.author?.lastName">{{ c.author?.email || c.author }}</span>
                  </span>
                  <span class="text-xs text-base-content/60">
                    {{ (c.createdAt || c.dateCreated || c.date) ? formatDate((c.createdAt || c.dateCreated || c.date)?.toString() || '') : '-' }}
                  </span>
                </div>
                <div class="text-sm prose prose-sm max-w-none" v-html="c.comment || c.content || c.message || ''"></div>
              </div>
            </div>
            <p v-else-if="!regulation.reason" class="text-sm text-base-content/50 italic mt-2">Aucun motif ou commentaire.</p>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      :show-modal="showModalCancel"
      title="Annuler la régularisation"
      body="Annuler cette régularisation ?"
      confirm-text="Annuler la régularisation"
      cancel-text="Fermer"
      confirm-variant="error"
      @confirm="onConfirmCancel"
      @cancel="showModalCancel = false"
    />
  </div>
</template>
